import React, { useRef, useState, useCallback } from 'react'
import cx from 'classnames'
import 'filepond/dist/filepond.min.css'
import { FilePond, registerPlugin } from 'react-filepond'
import CTA from '@/ui/CTA'
import { Icons } from '@/ui/icon'
import FileUploaded from '@/ui/FileUploaded'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import { uniqBy } from 'lodash'

registerPlugin(FilePondPluginFileValidateType)

interface ButtonProps {
  text: string
  iconName?: (typeof Icons)[keyof typeof Icons]
}

type FileCallback = (fileList: File[] | null) => void
type AsyncFileCallback = (fileList: File[] | null) => Promise<void>

interface BaseProps {
  value?: File[]
  label?: string
  labelIdle: string
  buttonProps?: ButtonProps
  showFilesList?: boolean
  isDisabled?: boolean
  'data-cy'?: string
  align?: 'left' | 'center' | 'right'
  coverDirection?: 'row' | 'row-reverse'
  acceptedFileTypes?: string[]
  allowMultiple?: boolean
  variant?: 'knowledge-base' | 'use-case'
}

interface UploaderModeProps extends BaseProps {
  mode: 'uploader'
  // Receives only the newly selected files
  onFilesUpload: AsyncFileCallback
}

interface InputModeProps extends BaseProps {
  mode: 'input'
  // Receives the selected files + previous files (state already calculated)
  onFilesUpdate: FileCallback
}

type Props = UploaderModeProps | InputModeProps

type DebouncedFileSelectProps =
  | {
      mode: 'uploader'
      callback: AsyncFileCallback
      pondRef: React.RefObject<FilePond>
    }
  | {
      mode: 'input'
      callback: FileCallback
      pondRef: React.RefObject<FilePond>
    }

/**
 * FILE UPLOADER MODES:
 *
 * UPLOADER MODE ('uploader'):
 * - Used for file upload scenarios where you want to process only newly selected files
 * - Callback receives only the files that were just selected by the user
 * - Callback is async (supports upload operations)
 * - No file list is shown (files are processed and cleared)
 *
 * INPUT MODE ('input'):
 * - Used for file selection scenarios where you want to maintain a persistent list
 * - Callback receives ALL files currently in FilePond (existing + new)
 * - Callback is sync (supports immediate state updates)
 * - File list is automatically shown with removal capabilities
 * - Automatic deduplication by filename to prevent duplicates
 *
 * ================================================================================
 *
 * DEBOUNCING HOOK:
 * This hook solves a critical issue with FilePond's file processing behavior:
 *
 * PROBLEM: FilePond calls the `process` function once per file, even when multiple
 * files are selected simultaneously. This leads to multiple individual calls instead
 * of a single batch operation.
 *
 * SOLUTION: This hook accumulates files and their corresponding FilePond callbacks
 * (load/error) over a short debounce period (50ms), then performs the appropriate
 * action based on the mode.
 *
 * BEHAVIOR:
 * 1. Each file gets added to an accumulation array along with its callbacks
 * 2. A 50ms debounce timer is reset on each new file
 * 3. When the timer expires:
 *    - UPLOADER MODE: Processes only the newly accumulated files (async)
 *    - INPUT MODE: Gets all files from FilePond, deduplicates, and updates state (sync)
 * 4. On success: all load callbacks are called to mark files as processed in FilePond
 * 5. On error: all error callbacks are called to mark files as failed in FilePond
 * 6. The accumulation arrays are reset for the next batch
 *
 * This ensures optimal performance while maintaining FilePond's expected UI behavior.
 */
function useDebouncedFileSelect({
  mode,
  callback,
  pondRef,
  setIsLoading,
}: DebouncedFileSelectProps & { setIsLoading: (loading: boolean) => void }) {
  const accumulatedFiles = useRef<File[]>([])
  const loadCallbacks = useRef<Array<Function>>([])
  const errorCallbacks = useRef<Array<Function>>([])
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const addFile = useCallback(
    (file: File, loadCallback: Function, errorCallback: Function) => {
      accumulatedFiles.current.push(file)
      loadCallbacks.current.push(loadCallback)
      errorCallbacks.current.push(errorCallback)

      // Clear existing timeout and set a new one
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }

      // Debounce with minimal delay (50ms)
      debounceTimeout.current = setTimeout(async () => {
        if (accumulatedFiles.current.length > 0) {
          try {
            switch (mode) {
              case 'input': {
                const filesToProcess =
                  pondRef.current
                    ?.getFiles()
                    .map((f) => f.file as File)
                    .filter((f) => f !== null) || []

                const uniqueFiles = uniqBy(filesToProcess, 'name')

                callback(uniqueFiles)

                setIsLoading(false)

                // Force update to keep pond in sync with state
                pondRef.current!.forceUpdate()
                break
              }

              case 'uploader': {
                const filesToProcess = accumulatedFiles.current
                await callback(filesToProcess)

                setIsLoading(false)
              }
            }

            // Call all success callbacks - each file gets marked as loaded
            loadCallbacks.current.forEach((loadCallback, index) => {
              loadCallback(accumulatedFiles.current[index])
            })
          } catch (error) {
            // Call all error callbacks - each file gets marked as failed
            errorCallbacks.current.forEach((errorCallback) => {
              errorCallback('Error al procesar el archivo')
            })

            setIsLoading(false)
          } finally {
            // Reset all accumulation arrays for next batch
            accumulatedFiles.current = []
            loadCallbacks.current = []
            errorCallbacks.current = []
          }
        }
      }, 50)
    },
    [mode, callback, pondRef, setIsLoading],
  )

  return { addFile }
}

export default function FileUploader(props: Props) {
  const {
    mode,
    value = [],
    labelIdle,
    label,
    buttonProps = { text: 'Elegir archivos', iconName: Icons.Upload },
    isDisabled = false,
    'data-cy': dataCy,
    align = 'center',
    coverDirection = 'row',
    acceptedFileTypes,
    allowMultiple = true,
    variant,
  } = props

  const pond = useRef<FilePond>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { addFile } = useDebouncedFileSelect(
    mode === 'uploader'
      ? {
          mode: 'uploader',
          callback: props.onFilesUpload,
          pondRef: pond,
          setIsLoading,
        }
      : {
          mode: 'input',
          callback: props.onFilesUpdate,
          pondRef: pond,
          setIsLoading,
        },
  )

  const handleBrowseClick = () => {
    pond.current?.browse()
  }

  const handleDragEvent = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e: React.DragEvent) => {
    handleDragEvent(e)
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    handleDragEvent(e)
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return
    }
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    handleDragEvent(e)
    setIsDragging(false)
  }

  const handleFileRemove = useCallback(
    async (fileToRemove: File, index: number) => {
      pond.current!.removeFiles([index])

      if (mode === 'input') {
        const remainingFiles = value.filter((f) => f !== fileToRemove)
        await (props as InputModeProps).onFilesUpdate(remainingFiles)
      }
    },
    [value, mode, props],
  )

  return (
    <div
      className={cx('filepond-container', {
        'is-disabled': isDisabled,
        [`variant-${variant}`]: variant,
      })}
    >
      {label && <label className="filepond-label">{label}</label>}
      <div
        className="filepond-uploader"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragEvent}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <FilePond
          name="files"
          ref={pond}
          server={{
            process: async (_fieldName, file, _metadata, load, error) => {
              if (file instanceof File) {
                // Add file to debounced batch
                addFile(file, load, error)
              } else {
                error('Formato de archivo no válido')
              }
            },
          }}
          allowRevert={false}
          allowMultiple={allowMultiple}
          maxFiles={25}
          labelIdle={labelIdle}
          labelFileProcessing="Subiendo..."
          labelFileProcessingComplete="Fichero subido"
          labelTapToCancel="Pulsa para cancelar"
          disabled={isDisabled || isLoading}
          acceptedFileTypes={acceptedFileTypes}
          onaddfilestart={() => setIsLoading(true)}
          // NOTE: @Antonio - Remove credits (https://pqina.nl/filepond/docs/api/instance/properties/#disabling-credits)
          credits={false}
        />
        <div
          className={cx('filepond-cover', align, {
            'is-dragging': isDragging,
          })}
        >
          <div className={cx('filepond-cover-content', coverDirection)}>
            <CTA
              data-cy={dataCy}
              variant="system"
              size="medium"
              iconLeftName={buttonProps.iconName}
              onClick={handleBrowseClick}
              isDisabled={isDisabled || isLoading}
              isLoading={isLoading}
            >
              {buttonProps.text}
            </CTA>
            <p>{labelIdle}</p>
          </div>
        </div>

        {mode === 'input' && value.length > 0 && (
          <div className="filepond-files">
            <ul className="filepond-files-list">
              {value.map((file, index) => (
                <li key={file.name}>
                  <FileUploaded
                    variant="light"
                    icon={Icons.Document}
                    onClick={() => handleFileRemove(file, index)}
                    disabled={isLoading}
                  >
                    {file.name}
                  </FileUploaded>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
